/**
 * /tenants/$slug/agents/$agentId — detail route shim.
 */

import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { fetchAgent } from "@/features/agents/queries";
import { queryKeys } from "@/lib/query-keys";
import { AgentDetailPage } from "@/features/agents/pages/agent-detail-page";

// The detail page is a tabbed registry hub. Every bit of view state lives in the
// URL so a link is deep-linkable and survives refresh:
//   - tab          selects Overview vs Activity (clean edge decisions) vs Audit
//                  trail (the verbose tamper-evident feed), so "show me agent X's
//                  activity" is a shareable link, not a click-to-reach-only panel.
//   - evidence_jti deep-links the evidence viewer (on the "Audit trail & evidence"
//                  tab, which it force-selects) to a specific grant's chain — the
//                  grant dashboard's click-through sets it; any tab click clears it.
//   - evidence_seq (with evidence_jti) anchors the viewer to ONE decision in that
//                  chain — the activity decisions table sets it so "verify THIS
//                  decision" lands on the exact signed row, not just the grant.
//   - preset/result/offset drive the Audit trail tab's filters (the subject scope
//                  itself is the route param, never a URL filter).
const searchSchema = z.object({
  tab: z.enum(["overview", "activity", "audit"]).optional(),
  evidence_jti: z.string().optional(),
  evidence_seq: z.coerce.number().int().nonnegative().optional(),
  preset: z.enum(["15m", "1h", "24h", "7d"]).optional(),
  result: z.enum(["success", "failure", "denied"]).optional(),
  offset: z.coerce.number().int().nonnegative().optional(),
});

export const Route = createFileRoute("/_authed/tenants/$slug/agents/$agentId")({
  validateSearch: searchSchema,
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData({
      queryKey: queryKeys.agents.detail(context.tenant.id, params.agentId),
      queryFn: () => fetchAgent(context.tenant.id, params.agentId),
    }),
  component: AgentDetailPage,
});
